/**
 * Sound Service for QuickBill POS App
 * 
 * Manages audio feedback for user interactions including:
 * - Transaction completion sounds
 * - Button click feedback
 * - Error/warning notifications
 * - Barcode scan sounds
 * 
 * Uses Web Audio API with fallback to HTML5 Audio
 */

class SoundService {
  constructor() {
    this.audioContext = null
    this.sounds = new Map()
    this.enabled = true
    this.volume = 0.7
    
    // Initialize audio context
    this.initAudioContext()
    
    // Load sound settings from localStorage
    this.loadSettings()
    
    // Preload common sounds
    this.loadSounds()
  }

  initAudioContext() {
    try {
      // Create AudioContext with user gesture support
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
      
      // Handle autoplay policy - resume context on user interaction
      if (this.audioContext.state === 'suspended') {
        const resumeAudio = () => {
          this.audioContext.resume()
          document.removeEventListener('click', resumeAudio)
          document.removeEventListener('touchstart', resumeAudio)
        }
        document.addEventListener('click', resumeAudio)
        document.addEventListener('touchstart', resumeAudio)
      }
    } catch (error) {
      console.warn('Web Audio API not supported, falling back to HTML5 Audio')
      this.audioContext = null
    }
  }

  loadSettings() {
    const settings = localStorage.getItem('quickbill_sound_settings')
    if (settings) {
      try {
        const parsed = JSON.parse(settings)
        this.enabled = parsed.enabled !== false // Default to true
        this.volume = parsed.volume || 0.7
      } catch (error) {
        console.warn('Error loading sound settings:', error)
      }
    }
  }

  saveSettings() {
    const settings = {
      enabled: this.enabled,
      volume: this.volume
    }
    localStorage.setItem('quickbill_sound_settings', JSON.stringify(settings))
  }

  async loadSounds() {
    // Define sound file paths (fallback to generated tones if files not found)
    const soundFiles = {
      success: '/audio/transaction-complete.mp3',
      click: '/audio/button-click.mp3', 
      error: '/audio/error-alert.mp3',
      scan: '/audio/scan-beep.mp3',
      payment: '/audio/transaction-complete.mp3',
      addCart: '/audio/item-added.mp3',
      remove: '/audio/item-removed.mp3',
      print: '/audio/print-success.mp3'
    }

    // Try to load audio files, fallback to generated tones
    for (const [key, filePath] of Object.entries(soundFiles)) {
      try {
        const audio = new Audio(filePath)
        audio.volume = this.volume
        
        // Test if file exists by trying to load it
        await new Promise((resolve, reject) => {
          audio.addEventListener('canplaythrough', resolve, { once: true })
          audio.addEventListener('error', reject, { once: true })
          audio.load()
        })
        
        this.sounds.set(key, audio)
      } catch (error) {
        console.warn(`Audio file ${filePath} not found, using generated tone for ${key}`)
        
        // Fallback to generated tones
        let toneData
        switch(key) {
          case 'success':
          case 'payment':
            toneData = this.generateTone(800, 0.15, 'sine').concat(this.generateTone(1000, 0.15, 'sine'))
            break
          case 'click':
            toneData = this.generateTone(400, 0.05, 'square')
            break
          case 'error':
            toneData = this.generateTone(200, 0.3, 'square')
            break
          case 'scan':
            toneData = this.generateTone(600, 0.1, 'sawtooth')
            break
          case 'addCart':
            toneData = this.generateTone(500, 0.08, 'sine')
            break
          case 'remove':
            toneData = this.generateTone(300, 0.1, 'triangle')
            break
          case 'print':
            toneData = this.generateTone(750, 0.12, 'sine')
            break
          default:
            toneData = this.generateTone(440, 0.1, 'sine')
        }
        
        // Convert generated tone to Audio object
        const audioBuffer = await this.createAudioBuffer(toneData)
        this.sounds.set(key, audioBuffer)
      }
    }
  }

  generateTone(frequency, duration, type = 'sine') {
    if (!this.audioContext) return []
    
    const sampleRate = this.audioContext.sampleRate
    const numSamples = Math.floor(sampleRate * duration)
    const buffer = new Float32Array(numSamples)
    
    for (let i = 0; i < numSamples; i++) {
      const time = i / sampleRate
      let value = 0
      
      switch (type) {
        case 'sine':
          value = Math.sin(2 * Math.PI * frequency * time)
          break
        case 'square':
          value = Math.sign(Math.sin(2 * Math.PI * frequency * time))
          break
        case 'sawtooth':
          value = 2 * (time * frequency - Math.floor(0.5 + time * frequency))
          break
        case 'triangle':
          value = 2 * Math.abs(2 * (time * frequency - Math.floor(time * frequency + 0.5))) - 1
          break
      }
      
      // Apply envelope for smoother sound
      const envelope = Math.exp(-3 * time / duration)
      buffer[i] = value * envelope * this.volume
    }
    
    return Array.from(buffer)
  }

  async createAudioBuffer(samples) {
    if (!this.audioContext || !samples.length) return null
    
    const buffer = this.audioContext.createBuffer(1, samples.length, this.audioContext.sampleRate)
    const channelData = buffer.getChannelData(0)
    
    for (let i = 0; i < samples.length; i++) {
      channelData[i] = samples[i]
    }
    
    return buffer
  }

  play(soundName, options = {}) {
    if (!this.enabled || !this.sounds.has(soundName)) {
      return Promise.resolve()
    }

    try {
      const sound = this.sounds.get(soundName)
      
      // Check if it's an HTML5 Audio object or Web Audio buffer
      if (sound instanceof Audio) {
        return this.playHTML5Audio(soundName, options)
      } else if (this.audioContext && sound) {
        return this.playWebAudio(soundName, options)
      } else {
        return Promise.resolve()
      }
    } catch (error) {
      console.warn(`Error playing sound ${soundName}:`, error)
      return Promise.resolve()
    }
  }

  playWebAudio(soundName, options = {}) {
    const buffer = this.sounds.get(soundName)
    if (!buffer || !this.audioContext) return Promise.resolve()

    const source = this.audioContext.createBufferSource()
    const gainNode = this.audioContext.createGain()
    
    source.buffer = buffer
    source.connect(gainNode)
    gainNode.connect(this.audioContext.destination)
    
    // Apply volume
    const volume = (options.volume || 1) * this.volume
    gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime)
    
    // Play the sound
    source.start(0)
    
    return new Promise((resolve) => {
      source.onended = resolve
      setTimeout(resolve, buffer.duration * 1000 + 100) // Fallback timeout
    })
  }

  playHTML5Audio(soundName, options = {}) {
    const audio = this.sounds.get(soundName)
    
    if (audio instanceof Audio) {
      // Use stored Audio object (from audio files)
      const clonedAudio = audio.cloneNode()
      clonedAudio.volume = (options.volume || 1) * this.volume
      return clonedAudio.play().catch(() => {
        // Ignore play errors (autoplay policy)
      })
    } else {
      // Fallback - create new Audio with generated beep
      const newAudio = new Audio()
      newAudio.volume = (options.volume || 1) * this.volume
      
      const frequency = {
        success: 800,
        click: 400,
        error: 200,
        scan: 600,
        payment: 660,
        addCart: 500,
        remove: 300,
        print: 750
      }[soundName] || 400

      const duration = 0.1
      newAudio.src = `data:audio/wav;base64,${this.generateSimpleBeep(frequency, duration)}`
      
      return newAudio.play().catch(() => {
        // Ignore play errors (autoplay policy)
      })
    }
  }

  generateSimpleBeep(frequency, duration) {
    // Generate a simple WAV file as base64
    // This is a simplified version for fallback
    return 'UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmYaAC2Q1fPTgTEFJHfH8N2QQAoUXrTp66hVFApGn+DyvmYaAC2Q1fPTgTEFJHfH8N2QQAoUXrTp66hVFApGn+DyvmYaAC2Q1fPTgTEFJHfH8N2QQAoUXrTp66hVFApGn+DyvmYaAC2Q1fPTgTEFJHfH8N2QQAoUXrTp66hVFApGn+DyvmYaAC2Q1fPTgTEFJHfH8N2QQAoUXrTp66hVFApGn+DyvmYaAC2Q1fPTgTEFJHfH8N2QQAoUXrTp66hVFApGn+DyvmYaAC2Q1fPTgTEFJHfH8N2QQAoUXrTp66hVFApGn+DyvmYaAC2Q1fPTgTEF'
  }

  // Public API methods
  setEnabled(enabled) {
    this.enabled = enabled
    this.saveSettings()
  }

  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume))
    
    // Update volume for any loaded Audio objects
    for (const sound of this.sounds.values()) {
      if (sound instanceof Audio) {
        sound.volume = this.volume
      }
    }
    
    this.saveSettings()
  }

  isEnabled() {
    return this.enabled
  }

  getVolume() {
    return this.volume
  }

  // Convenience methods for common actions
  playSuccess(options) { return this.play('success', options) }
  playClick(options) { return this.play('click', options) }
  playError(options) { return this.play('error', options) }
  playScan(options) { return this.play('scan', options) }
  playPayment(options) { return this.play('payment', options) }
  playAddCart(options) { return this.play('addCart', options) }
  playRemove(options) { return this.play('remove', options) }
  playPrint(options) { return this.play('print', options) }
}

// Export singleton instance
const soundService = new SoundService()
export default soundService